import {
  GeofenceTag,
  GeofenceTrigger_CalculateForJob,
  GeofenceType,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Geofence as SiteGeofence } from '~hooks/useSites/models';
import { type JobSiteUsage, type JobSiteUsageEntry } from '~hooks/useSiteUsages/models';

export interface GeofenceOverride {
  /** Stable key for React rendering and selection (geofence ID, or generated for new ones). */
  key: string;
  /** Original geofence name from the NBG record (e.g. "Moving Geofence for ..."). */
  name?: string;
  /** Tag differentiating this geofence (null for primary / SITE). */
  tag: string | null;
  geofenceType: GeofenceType;
  /** Polygon vertices for map editing and API (when geofenceType === 'polygon'). */
  polygonCoordinates: { lat: number; lng: number }[];
  /** Circle center for map and API (when geofenceType === 'circle'). */
  circleCenter: { lat: number; lng: number } | null;
  /** Circle radius in meters (when geofenceType === 'circle'). */
  circleRadius: number | null;
  /** Whether this geofence moves with a moving site (not editable on map). */
  movingGeofence?: boolean;
}

export interface SiteOverride {
  name: string;
  fullAddress: string;
  waypointType: WaypointType;
  /** Site ID used for linking geofences back to their site in API calls. */
  siteId?: string;
  /** Present when built from site_usages — shows how each site is used across loads. */
  usages?: JobSiteUsageEntry[];
  /** All geofences for this site. Index 0 is the primary. */
  geofences: GeofenceOverride[];
  /** Index into `geofences` of the currently selected geofence for map editing. */
  selectedGeofenceIndex: number;
}

export const DEFAULT_CIRCLE_RADIUS_M = 100;
/** ~100m offset in degrees for a default polygon triangle. */
const DEFAULT_POLYGON_OFFSET = 0.001;

function geofenceToOverride(geofence: SiteGeofence | null): GeofenceOverride {
  const base: GeofenceOverride = {
    key: geofence?.id ?? crypto.randomUUID(),
    name: geofence?.name ?? undefined,
    tag: geofence?.tag || null,
    geofenceType: GeofenceType.POLYGON,
    polygonCoordinates: [],
    circleCenter: null,
    circleRadius: null,
  };
  if (!geofence) return base;
  if (geofence.type === GeofenceType.POLYGON && geofence.geojson) {
    const coords = geofence.geojson.coordinates as unknown as [number, number][][];
    const ring = coords?.[0];
    if (ring?.length) {
      return {
        ...base,
        geofenceType: GeofenceType.POLYGON,
        polygonCoordinates: ring.map(([lng, lat]) => ({ lat, lng })),
      };
    }
  }
  if (geofence.type === GeofenceType.CIRCLE && geofence.circleCenter) {
    return {
      ...base,
      geofenceType: GeofenceType.CIRCLE,
      circleCenter: {
        lat: geofence.circleCenter.lat,
        lng: geofence.circleCenter.lon,
      },
      circleRadius: geofence.circleRadius ?? null,
    };
  }
  return base;
}

/** Default triangle matching MapV2's initial polygon shape. */
export function defaultPolygonAround(center: {
  lat: number;
  lng: number;
}): { lat: number; lng: number }[] {
  const d = DEFAULT_POLYGON_OFFSET;
  return [
    { lat: center.lat + d, lng: center.lng },
    { lat: center.lat - d, lng: center.lng + d },
    { lat: center.lat - d, lng: center.lng - d },
  ];
}

/** Build API geofences array from site overrides (for naive/accuracy_strict). */
export function buildGeofencesForApi(
  overrides: SiteOverride[],
): GeofenceTrigger_CalculateForJob['geofences'] {
  const out: NonNullable<GeofenceTrigger_CalculateForJob['geofences']> = [];
  for (const site of overrides) {
    const fallbackName = t(`waypoint_type.${site.waypointType}`);
    for (const gf of site.geofences) {
      const name = gf.name || site.name || fallbackName;
      if (gf.geofenceType === GeofenceType.POLYGON && gf.polygonCoordinates.length >= 3) {
        const ring = gf.polygonCoordinates.map((c) => [c.lng, c.lat] as [number, number]);
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (ring.length < 2 || first[0] !== last[0] || first[1] !== last[1]) {
          ring.push(ring[0]);
        }
        out.push({
          geofence: {
            name,
            type: GeofenceType.POLYGON,
            geojson: { type: 'Polygon', coordinates: [ring] },
          },
          tag: (gf.tag as GeofenceTag) ?? undefined,
          site_id: site.siteId,
          ...(gf.movingGeofence && { moving_geofence: true }),
        });
      } else if (
        gf.geofenceType === GeofenceType.CIRCLE &&
        gf.circleCenter &&
        gf.circleRadius !== null &&
        gf.circleRadius > 0
      ) {
        out.push({
          geofence: {
            name,
            type: GeofenceType.CIRCLE,
            circle_center: {
              lat: gf.circleCenter.lat,
              lon: gf.circleCenter.lng,
            },
            circle_radius: gf.circleRadius,
          },
          tag: (gf.tag as GeofenceTag) ?? undefined,
          site_id: site.siteId,
          ...(gf.movingGeofence && { moving_geofence: true }),
        });
      }
    }
  }
  return out.length ? out : undefined;
}

export function buildSiteOverridesFromSiteUsages(
  siteUsages: JobSiteUsage[],
): SiteOverride[] {
  return siteUsages.map((su) => {
    return {
      name: su.site.name ?? '',
      fullAddress: su.site.fullAddress ?? '',
      waypointType: su.usages[0]?.waypointType ?? WaypointType.PICKUP,
      siteId: su.site.id,
      usages: su.usages,
      geofences: [
        ...(su.site.nextBillionGeofence
          ? [
              {
                ...geofenceToOverride(su.site.nextBillionGeofence),
                ...(su.site.nextBillionGeofence.movingGeofence && {
                  movingGeofence: true,
                }),
              },
            ]
          : []),
        ...su.additionalGeofences.map((g) => ({
          ...geofenceToOverride(g),
          ...(g.movingGeofence && { movingGeofence: true }),
        })),
      ],
      selectedGeofenceIndex: 0,
    };
  });
}
